import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import { getAdminUsers, updateUserStatus, updateUserRole, updateUserFilingApproval, resetUserTwoFactor, AdminUser, AdminUsersResponse } from "../services/userService"
import { RootState } from "../store/store"
import ConfirmDialog from "../components/ui/ConfirmDialog"
import { initialsOf } from "../utils/initials"
import { FiLoader, FiMoreHorizontal, FiSearch, FiShield } from "react-icons/fi"

const PAGE_SIZE = 20

type Filter = "all" | "admins" | "inactive" | "unverified"

const FILTERS: Array<{ value: Filter; label: string; matches: (user: AdminUser) => boolean }> = [
  { value: "all", label: "All", matches: () => true },
  { value: "admins", label: "Admins", matches: (user) => user.isAdmin },
  { value: "inactive", label: "Inactive", matches: (user) => !user.active },
  { value: "unverified", label: "Not verified", matches: (user) => !user.verified },
]

type Tone = "ok" | "neutral" | "warn"

const FLAG_TONES: Record<Tone, string> = {
  ok: "bg-[#E6F4EA] text-[#14602B]",
  neutral: "bg-[#EEF1F4] text-[#4A5565]",
  warn: "bg-[#FFF1E0] text-[#8A3B00]",
}

function Flag({ tone, children }: { tone: Tone; children: string }) {
  return (
    <span className={`inline-flex whitespace-nowrap rounded-md px-2 py-[3px] text-xs font-semibold ${FLAG_TONES[tone]}`}>
      {children}
    </span>
  )
}

function Avatar({ user }: { user: AdminUser }) {
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        user.isAdmin ? "bg-dantzBlue text-white" : "bg-appAvatar text-dantzBlue"
      }`}
    >
      {initialsOf(`${user.firstName} ${user.lastName}`)}
    </span>
  )
}

interface MenuState {
  user: AdminUser
  top: number
  left: number
}

interface MenuItem {
  label: string
  danger?: boolean
  onSelect: () => void
}

function RowMenu({
  menu,
  groups,
  onClose,
}: {
  menu: MenuState
  groups: Array<{ title: string; items: MenuItem[] }>
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose()
    }
    function onPointer(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onPointer)
    window.addEventListener("scroll", onClose, true)
    window.addEventListener("resize", onClose)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onPointer)
      window.removeEventListener("scroll", onClose, true)
      window.removeEventListener("resize", onClose)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      role="menu"
      style={{ top: menu.top, left: menu.left }}
      className="fixed z-50 w-60 rounded-xl border border-appLine bg-white p-1.5 shadow-[0_12px_32px_rgba(15,30,50,0.16)]"
    >
      {groups.map((group) => (
        <div key={group.title} className="flex flex-col">
          <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-appMuted">
            {group.title}
          </p>
          {group.items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                onClose()
                item.onSelect()
              }}
              className={`rounded-lg px-3 py-2 text-left text-[13px] font-medium hover:bg-appBg ${
                item.danger ? "text-[#B42318]" : "text-appInk"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

function pageNumbers(page: number, totalPages: number) {
  const pages = new Set([1, totalPages, page - 1, page, page + 1])
  return Array.from(pages)
    .filter((value) => value >= 1 && value <= totalPages)
    .sort((a, b) => a - b)
}

export default function UsersPage() {
  const currentUser = useSelector((state: RootState) => state.auth.user)
  const [data, setData] = useState<AdminUsersResponse | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [statusTarget, setStatusTarget] = useState<AdminUser | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [approvalTarget, setApprovalTarget] = useState<AdminUser | null>(null)
  const [twoFactorTarget, setTwoFactorTarget] = useState<AdminUser | null>(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      setSearch(searchInput.trim())
    }, 400)
    return () => clearTimeout(timeout)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const result = await getAdminUsers(page, PAGE_SIZE, search)
        if (!cancelled) setData(result)
      } catch {
        if (!cancelled) setError("The user list could not be loaded.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [page, search])

  const totalPages = data?.totalPages ?? 1

  async function handleConfirmToggle() {
    if (!statusTarget) return

    const nextActive = !statusTarget.active
    try {
      await updateUserStatus(statusTarget._id, nextActive)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === statusTarget._id ? { ...user, active: nextActive } : user
              ),
            }
          : prev
      )
      setStatusTarget(null)
      setStatusError(null)
    } catch {
      setStatusError("The account status could not be changed.")
      setStatusTarget(null)
    }
  }

  async function handleConfirmRoleToggle() {
    if (!roleTarget) return

    const nextIsAdmin = !roleTarget.isAdmin
    try {
      await updateUserRole(roleTarget._id, nextIsAdmin)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === roleTarget._id ? { ...user, isAdmin: nextIsAdmin } : user
              ),
            }
          : prev
      )
      setRoleTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("The role could not be changed.")
      setRoleTarget(null)
    }
  }

  async function handleConfirmApprovalToggle() {
    if (!approvalTarget) return

    const next = !approvalTarget.canReleaseFilings
    try {
      await updateUserFilingApproval(approvalTarget._id, next)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === approvalTarget._id ? { ...user, canReleaseFilings: next } : user
              ),
            }
          : prev
      )
      setApprovalTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("The permission could not be changed.")
      setApprovalTarget(null)
    }
  }

  async function handleConfirmTwoFactorReset() {
    if (!twoFactorTarget) return

    try {
      await resetUserTwoFactor(twoFactorTarget._id)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === twoFactorTarget._id ? { ...user, twoFactorEnabled: false } : user
              ),
            }
          : prev
      )
      setTwoFactorTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("Two-factor authentication could not be reset.")
      setTwoFactorTarget(null)
    }
  }

  const [filter, setFilter] = useState<Filter>("all")
  const [menu, setMenu] = useState<MenuState | null>(null)

  const loaded = data?.users ?? []
  const activeFilter = FILTERS.find((entry) => entry.value === filter) ?? FILTERS[0]
  const shown = loaded.filter(activeFilter.matches)

  function openMenu(user: AdminUser, button: HTMLElement) {
    if (menu?.user._id === user._id) {
      setMenu(null)
      return
    }
    const rect = button.getBoundingClientRect()
    const menuWidth = 240
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8))
    setMenu({ user, top: rect.bottom + 6, left })
  }

  function menuGroups(user: AdminUser) {
    const groups: Array<{ title: string; items: MenuItem[] }> = [
      {
        title: "Permissions",
        items: [
          {
            label: user.isAdmin ? "Remove admin rights" : "Make admin",
            onSelect: () => setRoleTarget(user),
          },
          {
            label: user.canReleaseFilings ? "Revoke filing permission" : "Allow filing",
            onSelect: () => setApprovalTarget(user),
          },
        ],
      },
      {
        title: "Account",
        items: [
          ...(user.twoFactorEnabled
            ? [{ label: "Reset 2FA…", danger: true, onSelect: () => setTwoFactorTarget(user) }]
            : []),
          {
            label: user.active ? "Deactivate account…" : "Reactivate account",
            danger: user.active,
            onSelect: () => setStatusTarget(user),
          },
        ],
      },
    ]
    // Nobody changes their own role or account, only the filing permission
    if (user._id === currentUser?.id) return [{ title: "Permissions", items: groups[0].items.slice(1) }]
    return groups
  }

  const fullName = (user: AdminUser) => `${user.firstName} ${user.lastName}`

  return (
    <AppLayout crumb="Users" contentWidth="max-w-[1200px]">
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-1.5">
          <h1 className="font-heading text-[30px] font-semibold tracking-[-0.01em] text-dantzBlue">Users</h1>
          <p className="text-sm text-[#4A5565]">Manage accounts, roles and permissions.</p>
        </header>

        <section className="flex flex-col overflow-hidden rounded-2xl border border-appLine bg-white">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#EEF1F4] px-5 py-3.5">
            <div className="relative min-w-[240px] max-w-[420px] flex-[1_1_300px]">
              <FiSearch
                size={16}
                aria-hidden="true"
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-appMuted"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name, e-mail or company"
                aria-label="Search users"
                className="h-11 w-full rounded-[10px] border border-[#C8D1DB] bg-white pl-10 pr-3 text-sm text-appInk focus:border-dantzBlue focus:outline-none focus:ring-2 focus:ring-dantzBlue/20"
              />
            </div>

            <div role="group" aria-label="Filter users on this page" className="flex gap-1 rounded-[10px] bg-[#F1F3F6] p-1">
              {FILTERS.map((entry) => {
                const active = filter === entry.value

                return (
                  <button
                    key={entry.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter(entry.value)}
                    className={`flex h-9 items-center gap-2 rounded-lg px-3.5 text-[13px] ${
                      active
                        ? "bg-white font-semibold text-dantzBlue shadow-[0_1px_2px_rgba(15,30,50,0.12)]"
                        : "font-medium text-[#4A5565] hover:text-dantzBlue"
                    }`}
                  >
                    {entry.label}
                    <span className="text-xs text-appMuted">{loaded.filter(entry.matches).length}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {(statusError || roleError) && (
            <div role="alert" className="border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-700">
              {statusError || roleError}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-20 text-appMuted">
              <FiLoader size={18} className="animate-spin" />
              <span className="text-sm">Loading users...</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center py-20 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button onClick={() => setPage(1)} className="mt-3 text-sm text-dantzBlue underline">
                Try again
              </button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {shown.length === 0 ? (
                <p className="px-6 py-14 text-center text-sm text-[#4A5565]">
                  {loaded.length === 0 ? "No users found." : "No users match this filter."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1040px] text-left">
                    <thead>
                      <tr className="border-b border-[#EEF1F4] bg-[#FAFBFC]">
                        {["User", "Company", "Role", "Filing", "Status", "Security", "Created"].map((heading) => (
                          <th
                            key={heading}
                            className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.06em] text-appMuted"
                          >
                            {heading}
                          </th>
                        ))}
                        <th className="w-14 px-5 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((user) => {
                        const isSelf = user._id === currentUser?.id

                        return (
                          <tr key={user._id} className="border-b border-[#EEF1F4] last:border-b-0 hover:bg-[#F6F9FC]">
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <Avatar user={user} />
                                <div className="flex min-w-0 flex-col">
                                  <span className="flex items-center gap-2 text-sm">
                                    <span className={`font-semibold ${user.active ? "text-appInk" : "text-appMuted"}`}>
                                      {fullName(user)}
                                    </span>
                                    {isSelf && (
                                      <span className="rounded-md bg-[#FFF1E0] px-1.5 py-px text-[11px] font-semibold text-[#8A3B00]">
                                        You
                                      </span>
                                    )}
                                  </span>
                                  <span className="truncate text-[13px] text-[#4A5565]">{user.email}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-sm text-[#4A5565]">
                              {user.company || <span className="text-appMuted">–</span>}
                            </td>
                            <td className="px-5 py-3.5">
                              {user.isAdmin ? (
                                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-dantzBlue px-2.5 py-1 text-xs font-semibold text-white">
                                  <FiShield size={12} aria-hidden="true" className="text-[#F68B1F]" />
                                  Admin
                                </span>
                              ) : (
                                <span className="inline-flex whitespace-nowrap rounded-full bg-[#EEF1F4] px-2.5 py-1 text-xs font-semibold text-[#4A5565]">
                                  User
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              {user.canReleaseFilings ? (
                                <Flag tone="ok">Releases filings</Flag>
                              ) : (
                                <span className="text-sm text-appMuted">–</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <span
                                className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  user.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#EEF1F4] text-[#4A5565]"
                                }`}
                              >
                                {user.active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex flex-wrap gap-1.5">
                                <Flag tone={user.verified ? "ok" : "warn"}>
                                  {user.verified ? "Verified" : "Not verified"}
                                </Flag>
                                <Flag tone={user.twoFactorEnabled ? "ok" : "neutral"}>
                                  {user.twoFactorEnabled ? "2FA on" : "No 2FA"}
                                </Flag>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-[#4A5565]">
                              {new Date(user.createdAt).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <button
                                type="button"
                                aria-label={`Actions for ${fullName(user)}`}
                                aria-haspopup="menu"
                                aria-expanded={menu?.user._id === user._id}
                                onClick={(event) => openMenu(user, event.currentTarget)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#C8D1DB] bg-white text-dantzBlue hover:bg-appBg"
                              >
                                <FiMoreHorizontal size={18} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEF1F4] px-5 py-3.5">
                <p className="text-[13px] text-[#4A5565]">
                  Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, data.total)} of {data.total} users
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="h-9 rounded-lg px-3 text-[13px] font-medium text-[#4A5565] hover:bg-appBg disabled:opacity-40"
                    onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers(page, totalPages).map((number) => (
                    <button
                      key={number}
                      type="button"
                      aria-current={page === number ? "page" : undefined}
                      className={`h-9 min-w-9 rounded-lg px-3 text-[13px] font-semibold ${
                        page === number ? "bg-dantzBlue text-white" : "text-[#4A5565] hover:bg-appBg"
                      }`}
                      onClick={() => setPage(number)}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="h-9 rounded-lg px-3 text-[13px] font-medium text-[#4A5565] hover:bg-appBg disabled:opacity-40"
                    onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {menu && <RowMenu menu={menu} groups={menuGroups(menu.user)} onClose={() => setMenu(null)} />}

      {statusTarget && (
        <ConfirmDialog
          message={
            statusTarget.active
              ? `Deactivate ${fullName(statusTarget)}? They can no longer sign in.`
              : `Reactivate ${fullName(statusTarget)}?`
          }
          confirmLabel={statusTarget.active ? "Deactivate" : "Reactivate"}
          variant={statusTarget.active ? "danger" : "primary"}
          onCancel={() => setStatusTarget(null)}
          onConfirm={handleConfirmToggle}
        />
      )}

      {approvalTarget && (
        <ConfirmDialog
          message={
            approvalTarget.canReleaseFilings
              ? `Revoke the permission of ${fullName(approvalTarget)} to release filings?`
              : `Allow ${fullName(approvalTarget)} to release reviewed filings?`
          }
          confirmLabel={approvalTarget.canReleaseFilings ? "Revoke" : "Allow"}
          variant={approvalTarget.canReleaseFilings ? "danger" : "primary"}
          onCancel={() => setApprovalTarget(null)}
          onConfirm={handleConfirmApprovalToggle}
        />
      )}

      {twoFactorTarget && (
        <ConfirmDialog
          message={`Reset two-factor authentication for ${fullName(twoFactorTarget)}? They will be signed out and can set it up again.`}
          confirmLabel="Reset 2FA"
          variant="danger"
          onCancel={() => setTwoFactorTarget(null)}
          onConfirm={handleConfirmTwoFactorReset}
        />
      )}

      {roleTarget && (
        <ConfirmDialog
          message={
            roleTarget.isAdmin
              ? `Remove the admin rights of ${fullName(roleTarget)}?`
              : `Make ${fullName(roleTarget)} an admin?`
          }
          confirmLabel={roleTarget.isAdmin ? "Remove admin" : "Make admin"}
          variant={roleTarget.isAdmin ? "danger" : "primary"}
          onCancel={() => setRoleTarget(null)}
          onConfirm={handleConfirmRoleToggle}
        />
      )}
    </AppLayout>
  )
}
